import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  makeStyles,
} from '@material-ui/core';
import CheckIcon from '@material-ui/icons/Check';
import CloseIcon from '@material-ui/icons/Close';
import LocalShippingIcon from '@material-ui/icons/LocalShipping';
import RestaurantIcon from '@material-ui/icons/Restaurant';
import DoneAllIcon from '@material-ui/icons/DoneAll';
import * as React from 'react';
import { ExternalOrderLog, BoltOrderStatus } from 'interfaces';
import { ExternalOrderService } from 'services';

interface Props {
  log: ExternalOrderLog;
  onChanged: () => void;
  // Which platform this card belongs to. Drives the accept payload
  // (Bolt sends an absolute cook-time in seconds; Wolt sends an
  // adjusted_pickup_time ISO stamp), the reject shape, and the status
  // machine. Defaults to Bolt for backward safety.
  platform?: 'Bolt' | 'Wolt' | 'Website';
}

const useStyles = makeStyles((theme) => ({
  bar: {
    display: 'flex',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1.5),
    flexWrap: 'wrap',
  },
  hint: {
    marginTop: theme.spacing(1.5),
    color: theme.palette.text.secondary,
    fontSize: '0.78rem',
  },
  errorHint: {
    marginTop: theme.spacing(0.5),
    color: theme.palette.error.main,
    fontSize: '0.78rem',
    wordBreak: 'break-word',
  },
}));

type RejectReason =
  | 'do_not_wish_to_serve_this_client'
  | 'too_many_orders'
  | 'other';

/**
 * Status-aware action bar at the bottom of each Bolt preview card.
 * Buttons available depend on the current bolt_order_status:
 *
 *   null              -> Accept | Reject
 *   accepted          -> Mark Ready
 *   ready_for_pickup  -> Mark Picked Up
 *   picked_up         -> Mark Delivered
 *   rejected          -> (terminal)
 *   delivered         -> (terminal)
 *
 * Reject opens a small dialog for the required reason + optional comment.
 * The wall between this and our own orders/kitchen pipeline stays in
 * place — these calls only talk to Bolt's API.
 */
export const BoltStatusActions: React.FC<Props> = ({
  log,
  onChanged,
  platform = 'Bolt',
}) => {
  const classes = useStyles();
  const isWolt = platform === 'Wolt';
  // Our own storefront (2026-09-05). Simplest state machine of the three,
  // because there is no platform API to keep in sync:
  //   null -> Accept (waiting time, fires the kitchen) | Reject
  //   accepted -> Mark Picked Up (driver took it / customer collected)
  //   picked_up, rejected -> terminal
  const isWebsite = platform === 'Website';
  const [submitting, setSubmitting] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [rejectOpen, setRejectOpen] = React.useState(false);
  const [rejectReason, setRejectReason] =
    React.useState<RejectReason>('other');
  const [rejectComment, setRejectComment] = React.useState('');
  const [acceptOpen, setAcceptOpen] = React.useState(false);
  const [prepMinutes, setPrepMinutes] = React.useState<number>(25);
  const [acceptComment, setAcceptComment] = React.useState('');

  const status: BoltOrderStatus = (log.boltOrderStatus || null) as BoltOrderStatus;

  // Bolt's order types:
  //   delivery   = Bolt's courier delivers. Restaurant only does Accept + Mark Ready.
  //                Courier handles Picked Up and Delivered on their own app.
  //   takeaway   = Customer comes to collect. Restaurant marks Picked Up when
  //                handing the bag over. No Mark Delivered.
  // Restaurant Own Delivery (ROD) would need both Picked Up + Delivered, but
  // we explicitly opted out of ROD in the 2-way integration doc.
  const pp: any = log.parsedPayload ?? {};
  const orderType: string | undefined = pp.order_type ?? pp.orderType ?? undefined;
  const isDelivery = orderType === 'delivery';
  // Explicit customer-collection order. Deliberately NOT "!isDelivery":
  // Wolt orders parsed before 2026-07-20 froze order_type as "instant"
  // (we read the wrong field), and those are overwhelmingly home
  // deliveries — treating them as collection would offer an action Wolt
  // rejects. Only an explicit takeaway/eatin counts.
  const isCollection = orderType === 'takeaway' || orderType === 'eatin';
  // "Mark Picked Up" is a Bolt-takeaway ack; Wolt has no equivalent call.
  // Website: ONE hand-off action on every order type. Delivery = our
  // driver took the bag (customer's page flips to "on its way"); pickup =
  // customer walked out with it (their page deliberately does not change).
  const showMarkPickedUp = isWebsite || (!isWolt && !isDelivery);
  // Wolt: the merchant marks DELIVERED when the customer collects a
  // TAKEAWAY order. Home-delivery orders are closed by Wolt's own courier
  // — their API refuses it with "Cannot move home delivery purchase to
  // delivered state" (confirmed on a live order 2026-07-20). Bolt never
  // needs it (that's ROD, which we don't support).
  const showMarkDelivered = isWolt && isCollection;

  const run = async (label: string, fn: () => Promise<any>) => {
    if (submitting) return;
    setSubmitting(label);
    setError(null);
    try {
      await fn();
      onChanged();
    } catch (e) {
      setError((e as Error)?.message || 'Action failed');
    } finally {
      setSubmitting(null);
    }
  };

  // Accept now flows through a small dialog asking for prep time. Bolt's
  // `delay_time_in_seconds` is the ABSOLUTE total cook time, not an
  // addition. Minutes -> seconds happens here.
  // Bolt's 2-way doc caps cooking delay at 40 minutes; anything higher
  // would be rejected by their API.
  const handleAcceptConfirm = () => {
    setAcceptOpen(false);
    if (isWebsite) {
      // One call: quotes the customer's waiting time AND fires the
      // kitchen. Rolls itself back server-side if the fire fails.
      const minutes = Math.max(1, Math.round(prepMinutes || 25));
      run('accept', () =>
        ExternalOrderService.websiteAccept(log.id, { prepMinutes: minutes }),
      );
      return;
    }
    if (isWolt) {
      // Wolt wants an absolute "food ready for pickup" timestamp, not a
      // duration. No 40-min cap (that is a Bolt rule). Mirror the takeover
      // popup: now + prep minutes, as an ISO stamp.
      const minutes = Math.max(1, Math.round(prepMinutes || 25));
      const pickupAt = new Date(Date.now() + minutes * 60 * 1000).toISOString();
      // SCHEDULED (pre-)orders are ACKED with confirm-preorder; accept is
      // refused ahead of the preorder time (400 "Cannot move scheduled
      // order to production"). Our controller treats confirm-preorder as
      // an accept internally, so the kitchen bridge still runs.
      const isPreorder =
        (pp.fulfilmentTiming || pp.fulfilment_timing) === 'preorder';
      run('accept', () =>
        isPreorder
          ? ExternalOrderService.woltConfirmPreorder(log.id, {
              adjustedPickupTime: pickupAt,
            })
          : ExternalOrderService.woltAccept(log.id, {
              adjustedPickupTime: pickupAt,
            }),
      );
      return;
    }
    // Bolt's delay_time_in_seconds is the ABSOLUTE total cook time and is
    // capped at 40 minutes by their API.
    const minutes = Math.max(1, Math.min(40, Math.round(prepMinutes || 25)));
    run('accept', () =>
      ExternalOrderService.boltAccept(log.id, {
        delayTimeInSeconds: minutes * 60,
        orderDelayReason: acceptComment.trim() || undefined,
      }),
    );
  };

  const handleMarkReady = () =>
    run('ready', () =>
      isWolt
        ? ExternalOrderService.woltMarkReady(log.id)
        : ExternalOrderService.boltMarkReady(log.id),
    );

  const handleMarkPickedUp = () =>
    run('picked_up', () =>
      isWebsite
        ? ExternalOrderService.websiteMarkPickedUp(log.id)
        : ExternalOrderService.boltMarkPickedUp(log.id),
    );

  const handleMarkDelivered = () =>
    run('delivered', () =>
      isWolt
        ? ExternalOrderService.woltMarkDelivered(log.id)
        : ExternalOrderService.boltMarkDelivered(log.id),
    );

  const handleRejectConfirm = () => {
    setRejectOpen(false);
    if (isWebsite) {
      run('reject', () =>
        ExternalOrderService.websiteReject(log.id, {
          reason: rejectComment.trim() || rejectReason,
        }),
      );
      return;
    }
    if (isWolt) {
      // Wolt reject takes a single free-text reason. Use the typed
      // comment, else fall back to the dropdown value.
      run('reject', () =>
        ExternalOrderService.woltReject(log.id, {
          reason: rejectComment.trim() || rejectReason,
        }),
      );
      return;
    }
    run('reject', () =>
      ExternalOrderService.boltReject(log.id, {
        rejectReason,
        rejectComment: rejectComment.trim() || undefined,
      }),
    );
  };

  // Terminal states — just a label
  if (isWebsite && status === 'picked_up') {
    return (
      <Typography className={classes.hint}>
        {isDelivery
          ? '✓ Out for delivery · customer sees "on its way"'
          : '✓ Collected by customer'}
      </Typography>
    );
  }
  if (status === 'delivered') {
    return (
      <Typography className={classes.hint}>
        ✓ Delivered to customer · ack sent to {platform}
      </Typography>
    );
  }
  if (status === 'rejected') {
    return (
      <Typography className={classes.hint}>
        ✗ Rejected · ack sent to {platform}
      </Typography>
    );
  }
  // Wolt HOME DELIVERY: marking ready is our last action — their courier
  // takes it from there. Takeaway is NOT terminal here; we still owe a
  // Mark Delivered when the customer collects (handled below).
  if (isWolt && isDelivery && ['ready_for_pickup', 'picked_up'].includes(status || '')) {
    return (
      <Typography className={classes.hint}>
        ✓ Ready · handed to Wolt courier
      </Typography>
    );
  }

  return (
    <React.Fragment>
      <div className={classes.bar}>
        {status === null && (
          <React.Fragment>
            <Button
              size="small"
              variant="contained"
              style={{ background: '#43a047', color: 'white' }}
              startIcon={<CheckIcon />}
              disabled={!!submitting}
              onClick={() => {
                setPrepMinutes(25);
                setAcceptComment('');
                setAcceptOpen(true);
              }}
            >
              {submitting === 'accept' ? 'Accepting…' : 'Accept'}
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="secondary"
              startIcon={<CloseIcon />}
              disabled={!!submitting}
              onClick={() => setRejectOpen(true)}
            >
              Reject
            </Button>
          </React.Fragment>
        )}
        {status === 'accepted' && !isWebsite && (
          <Button
            size="small"
            variant="contained"
            color="primary"
            startIcon={<RestaurantIcon />}
            disabled={!!submitting}
            onClick={handleMarkReady}
          >
            {submitting === 'ready' ? 'Updating…' : 'Mark Ready for Pickup'}
          </Button>
        )}
        {status === 'accepted' && isWebsite && (
          <Button
            size="small"
            variant="contained"
            color="primary"
            startIcon={<LocalShippingIcon />}
            disabled={!!submitting}
            onClick={handleMarkPickedUp}
          >
            {submitting === 'picked_up'
              ? 'Updating…'
              : isDelivery
              ? 'Driver collected — mark on its way'
              : 'Customer collected — mark picked up'}
          </Button>
        )}
        {status === 'ready_for_pickup' && showMarkPickedUp && (
          <Button
            size="small"
            variant="contained"
            color="primary"
            startIcon={<LocalShippingIcon />}
            disabled={!!submitting}
            onClick={handleMarkPickedUp}
          >
            {submitting === 'picked_up' ? 'Updating…' : 'Mark Picked Up'}
          </Button>
        )}
        {showMarkDelivered &&
          (status === 'picked_up' ||
            (isWolt && status === 'ready_for_pickup')) && (
          <Button
            size="small"
            variant="contained"
            color="primary"
            startIcon={<DoneAllIcon />}
            disabled={!!submitting}
            onClick={handleMarkDelivered}
          >
            {submitting === 'delivered' ? 'Updating…' : 'Mark Delivered'}
          </Button>
        )}
      </div>

      {error && (
        <Typography className={classes.errorHint}>Error: {error}</Typography>
      )}

      <Dialog
        open={rejectOpen}
        onClose={() => setRejectOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Reject this {platform} order?</DialogTitle>
        <DialogContent>
          <FormControl fullWidth style={{ marginBottom: 12 }}>
            <InputLabel id="reject-reason-label">Reason</InputLabel>
            <Select
              labelId="reject-reason-label"
              value={rejectReason}
              onChange={(e) =>
                setRejectReason(e.target.value as RejectReason)
              }
            >
              <MenuItem value="too_many_orders">Too many orders</MenuItem>
              <MenuItem value="do_not_wish_to_serve_this_client">
                Do not wish to serve this client
              </MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
          </FormControl>
          <TextField
            label="Comment (optional)"
            value={rejectComment}
            onChange={(e) => setRejectComment(e.target.value)}
            multiline
            rows={2}
            fullWidth
            placeholder="e.g. Kitchen is closing in 10 minutes"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectOpen(false)} disabled={!!submitting}>
            Keep
          </Button>
          <Button
            onClick={handleRejectConfirm}
            color="secondary"
            variant="contained"
            disabled={!!submitting}
          >
            Reject order
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={acceptOpen}
        onClose={() => setAcceptOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Accept order — prep time?</DialogTitle>
        <DialogContent>
          <Typography
            variant="caption"
            color="textSecondary"
            style={{ display: 'block', marginBottom: 8 }}
          >
            {isWolt
              ? 'Minutes until the food is ready for pickup. Wolt uses this for the courier ETA. Pick a quick option or enter minutes.'
              : "Total cook time you're committing to. Bolt shows this to the customer and the courier. Pick a quick option or enter minutes."}
          </Typography>
          <div
            style={{
              display: 'flex',
              gap: 6,
              marginBottom: 12,
              flexWrap: 'wrap',
            }}
          >
            {[15, 25, 35, 40].map((m) => (
              <Button
                key={m}
                size="small"
                variant={prepMinutes === m ? 'contained' : 'outlined'}
                color="primary"
                onClick={() => setPrepMinutes(m)}
              >
                {m} min
              </Button>
            ))}
          </div>
          <TextField
            label="Prep time (minutes)"
            type="number"
            value={prepMinutes}
            onChange={(e) => {
              const v = parseInt(e.target.value, 10);
              setPrepMinutes(Number.isFinite(v) ? v : 0);
            }}
            inputProps={{ min: 1, max: isWolt ? 120 : 40, step: 1 }}
            fullWidth
            style={{ marginBottom: 12 }}
          />
          {!isWolt && (
            <TextField
              label="Reason (optional)"
              value={acceptComment}
              onChange={(e) => setAcceptComment(e.target.value)}
              multiline
              rows={2}
              fullWidth
              placeholder="e.g. Kitchen running busy"
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAcceptOpen(false)} disabled={!!submitting}>
            Cancel
          </Button>
          <Button
            onClick={handleAcceptConfirm}
            variant="contained"
            disabled={!!submitting || !prepMinutes || prepMinutes < 1}
            style={{ background: '#43a047', color: 'white' }}
          >
            Accept ({prepMinutes || 0} min)
          </Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
};
